import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { StarRounded } from "@mui/icons-material";

export default function InterviewFeedbackModal({
  open,
  interviewId,
  started,
  setStarted,
  step,
  setStep,
  questions,
  answers,
  setAnswers,
  apiError,
  onRetryUpload,
  onSubmitAnswer,
  onFinish,
}) {
  const [submittingStep, setSubmittingStep] = useState(false);

  const current = questions?.[step] || null;
  const qId = current?.id;
  const answer = qId ? answers?.[qId] : undefined;

  const ratingMeta = useMemo(
    () => ({
      1: { label: "Very Poor", color: "#ef4444", buttonBg: "#ef4444" },
      2: { label: "Poor", color: "#f59e0b", buttonBg: "#f59e0b" },
      3: { label: "Average", color: "#0ea5e9", buttonBg: "#0ea5e9" },
      4: { label: "Good", color: "#4f46e5", buttonBg: "#4f46e5" },
      5: { label: "Excellent", color: "#65a30d", buttonBg: "#65a30d" },
    }),
    [],
  );

  const canContinue = useMemo(() => {
    if (!started) return true;
    if (!current) return false;
    if (current.kind === "stars") return typeof answer === "number" && answer > 0;
    if (current.kind === "options") return typeof answer === "string" && answer.length > 0;
    return false;
  }, [answer, current, started]);

  const selectedMeta = useMemo(() => {
    if (!started || !current) return null;
    if (current.kind === "stars") {
      return ratingMeta[typeof answer === "number" ? answer : 0] || null;
    }
    if (current.kind === "options") {
      const opts = current.options || [];
      const idx = typeof answer === "string" ? opts.findIndex((o) => o === answer) : -1;
      if (idx < 0) return null;
      const raw = opts.length <= 1 ? 3 : 5 - (idx * 4) / (opts.length - 1);
      const score = Math.max(1, Math.min(5, Math.round(raw)));
      return ratingMeta[score] || null;
    }
    return null;
  }, [answer, current, ratingMeta, started]);

  const handleSelect = (value) => {
    if (!qId) return;
    setAnswers((prev) => ({ ...prev, [qId]: value }));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleContinue = async () => {
    if (!started) {
      setStarted(true);
      return;
    }
    const isLast = step >= (questions?.length || 0) - 1;

    if (!current || !qId) return;
    if (submittingStep) return;

    const questionText = current.text;
    const answerText =
      current.kind === "stars"
        ? ratingMeta[answer]?.label || ""
        : typeof answer === "string"
          ? answer
          : "";

    if (!isLast) {
      // Fire-and-forget for steps 1..6 (should not block UX).
      try {
        Promise.resolve(
          onSubmitAnswer?.({
            interviewId,
            question: questionText,
            answer: answerText,
          }),
        ).catch((e) => {
          console.error("[InterviewFeedbackModal] submit answer failed:", e);
        });
      } catch (e) {
        console.error("[InterviewFeedbackModal] submit answer failed:", e);
      }

      setStep((s) => s + 1);
      return;
    }

    // Last step: wait for feedback API before redirect, but don't block on failure.
    setSubmittingStep(true);
    try {
      await onSubmitAnswer?.({
        interviewId,
        question: questionText,
        answer: answerText,
      });
    } catch (e) {
      console.error("[InterviewFeedbackModal] final submit failed:", e);
      // continue anyway (errors should not block UX)
    } finally {
      setSubmittingStep(false);
    }

    await onFinish?.();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      scroll="body"
      PaperProps={{ sx: { borderRadius: 3, p: 0.5, overflow: "hidden" } }}
      onClose={() => {}}
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        {!started ? (
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Quick feedback
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
              We're just wrapping up your interview submission. Please hang tight and avoid closing or refreshing this page. 
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary" }}>
             In the meantime, we'd love your feedback - this will only take a minute.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Step {step + 1} of {questions?.length || 0}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {Array.from({ length: questions?.length || 0 }, (_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 22,
                      height: 4,
                      borderRadius: 999,
                      bgcolor: i === step ? "#4f46e5" : "rgba(15, 23, 42, 0.12)",
                      transition: "background-color 160ms ease",
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Typography variant="body1" fontWeight={900} sx={{ mt: 1 }}>
              {current?.text}
            </Typography>
            <Typography variant="caption" sx={{ mt: 0.5, color: "text.secondary" }}>
              {current?.kind === "stars" ? "Tap a star to rate your experience" : "Select one option"}
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* {apiError ? (
          <Alert
            severity="warning"
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Button
                size="small"
                variant="contained"
                onClick={onRetryUpload}
                sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 700 }}
              >
                Retry Upload
              </Button>
            }
          >
            {apiError}
          </Alert>
        ) : null} */}

        {started && current?.kind === "options" ? (
          <>
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              {(current.options || []).map((opt) => {
                const selected = answer === opt;
                const opts = current.options || [];
                const idx = opts.findIndex((o) => o === opt);
                const raw = opts.length <= 1 ? 3 : 5 - (idx * 4) / (opts.length - 1);
                const score = Math.max(1, Math.min(5, Math.round(raw)));
                const m = ratingMeta[score];
                return (
                  <Box
                    key={opt}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelect(opt)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelect(opt);
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      border: selected ? `1px solid ${m.color}aa` : "1px solid rgba(15, 23, 42, 0.16)",
                      backgroundColor: selected ? `${m.color}14` : "#fff",
                      px: 2,
                      py: 1.5,
                      cursor: "pointer",
                      userSelect: "none",
                      outline: "none",
                      transition: "box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease",
                      "&:hover": {
                        boxShadow: "0 10px 26px rgba(15, 23, 42, 0.10)",
                      },
                      "&:focus-visible": {
                        boxShadow: `0 0 0 4px ${m.color}2e`,
                        borderColor: `${m.color}aa`,
                      },
                    }}
                  >
                    <Typography variant="body2" fontWeight={selected ? 800 : 600} sx={{ color: selected ? m.color : "text.primary" }}>
                      {opt}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </>
        ) : null}

        {started && current?.kind === "stars" ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.25 }}>
              <Box sx={{ display: "flex", gap: 1.25, flexWrap: "nowrap" }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const isSelected = answer === n;
                  const m = ratingMeta[n];
                  return (
                    <Box
                      key={n}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelect(n)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelect(n);
                        }
                      }}
                      sx={{
                        width: { xs: 56, sm: 68 },
                        height: { xs: 56, sm: 68 },
                        borderRadius: 2,
                        border: "1px solid rgba(15, 23, 42, 0.18)",
                        backgroundColor: "#fff",
                        display: "grid",
                        placeItems: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        outline: "none",
                        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                        ...(isSelected && {
                          transform: "rotate(-6deg) scale(1.06)",
                          borderColor: "rgba(79, 70, 229, 0.55)",
                          boxShadow: "0 14px 35px rgba(15, 23, 42, 0.16)",
                        }),
                        "&:hover": {
                          boxShadow: isSelected
                            ? "0 14px 35px rgba(15, 23, 42, 0.16)"
                            : "0 10px 26px rgba(15, 23, 42, 0.12)",
                        },
                        "&:focus-visible": {
                          boxShadow: "0 0 0 4px rgba(79, 70, 229, 0.18)",
                          borderColor: "rgba(79, 70, 229, 0.55)",
                        },
                      }}
                    >
                      <StarRounded
                        sx={{
                          fontSize: { xs: 30, sm: 36 },
                          color: isSelected ? m.color : "rgba(15, 23, 42, 0.28)",
                          transition: "color 160ms ease",
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Typography
              variant="body2"
              fontWeight={800}
              sx={{
                textAlign: "center",
                color: selectedMeta?.color || "rgba(15, 23, 42, 0.5)",
                minHeight: 22,
                mb: 1.25,
              }}
            >
              {selectedMeta?.label || ""}
            </Typography>
          </>
        ) : null}

        {started ? (
          <Box sx={{ px: 0.5, mb: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={((step + 1) / (questions?.length || 1)) * 100}
              sx={{
                height: 4,
                borderRadius: 999,
                backgroundColor: "rgba(15, 23, 42, 0.10)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  backgroundColor: selectedMeta?.color || "#4f46e5",
                },
              }}
            />
          </Box>
        ) : null}

        <Typography variant="caption" mt={3} sx={{ color: "text.secondary", fontWeight: 600 }}>
          Your feedback helps improve the interview experience. Please do not close or refresh the page.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
        {/* {started && (
          <Button
            onClick={handleBack}
            disabled={step === 0 || submittingStep}
            color="secondary"
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 700 }}
          >
            Back
          </Button>
        )} */}

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!canContinue || submittingStep}
          color="primary"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 900,
            px: 4,
            minWidth: 220,
           
          }}
        >
          {started ? (submittingStep ? "Saving..." : "Continue") : "Start"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

